/**
 * @fileoverview Draft approval audit trail (MOR-529 Phase 2).
 *
 * Renders `listApprovalEvents` chronologically: an icon + actor + action verb
 * per row, with relative time and any note. Compact, muted styling — this is a
 * supporting detail panel, not a primary surface.
 */

import { useQuery } from "convex/react";
import { formatDistanceToNow } from "date-fns";
import {
  Ban,
  CheckCircle2,
  FilePlus2,
  Pencil,
  Send,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Spinner } from "@/ui/primitives/spinner";
import type { ApprovalEvent } from "./types";

const ACTION_META: Record<
  ApprovalEvent["action"],
  { icon: LucideIcon; verb: string; danger?: boolean }
> = {
  created: { icon: FilePlus2, verb: "created this draft" },
  edited: { icon: Pencil, verb: "edited the draft" },
  approved: { icon: CheckCircle2, verb: "approved the draft" },
  rejected: { icon: Ban, verb: "rejected the draft", danger: true },
  sent: { icon: Send, verb: "sent the message" },
  send_failed: { icon: TriangleAlert, verb: "send failed", danger: true },
};

/** Human label for the actor behind an event. */
function actorLabel(event: ApprovalEvent): string {
  if (event.actorType === "agent") return "Morgan";
  if (event.actorType === "system") return "System";
  return event.actorName ?? "A teammate";
}

export function ApprovalAuditLog({ draftId }: { draftId: Id<"mailDrafts"> }) {
  const events = useQuery(api.mail.drafts.listApprovalEvents, { draftId }) as
    ApprovalEvent[] | undefined;

  if (events === undefined) {
    return (
      <div className="flex justify-center py-3">
        <Spinner className="size-4 text-muted-foreground" />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <p className="py-2 text-xs text-muted-foreground">No activity yet.</p>
    );
  }

  return (
    <ol className="space-y-2">
      {events.map((event) => {
        const meta = ACTION_META[event.action];
        const Icon = meta.icon;
        return (
          <li key={event._id} className="flex items-start gap-2 text-xs">
            <Icon
              className={
                meta.danger
                  ? "mt-0.5 size-3.5 shrink-0 text-destructive"
                  : "mt-0.5 size-3.5 shrink-0 text-muted-foreground"
              }
            />
            <div className="min-w-0">
              <span className="text-foreground/80">
                <span className="font-medium">{actorLabel(event)}</span>{" "}
                {meta.verb}
              </span>
              <span className="text-muted-foreground">
                {" "}
                &middot;{" "}
                {formatDistanceToNow(event._creationTime, { addSuffix: true })}
              </span>
              {event.note && (
                <p className="mt-0.5 text-muted-foreground">{event.note}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
